import React from 'react';
import { Box, Divider, Typography } from '@mui/material';

/**
 * Parse inline JIRA wiki formatting into React nodes.
 * Handles *bold* and {{code}} within a line.
 */
function parseInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Match *bold* or {{code}} tokens
  const regex = /\*([^*]+)\*|\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      // *bold*
      nodes.push(
        <Box key={key++} component="strong" sx={{ fontWeight: 'bold' }}>
          {match[1]}
        </Box>
      );
    } else if (match[2] !== undefined) {
      // {{code}}
      nodes.push(
        <Box
          key={key++}
          component="code"
          sx={{
            bgcolor: 'action.hover',
            px: 0.5,
            borderRadius: 0.5,
            fontSize: '0.85em',
            fontFamily: 'monospace',
          }}
        >
          {match[2]}
        </Box>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length > 0 ? nodes : [text];
}

/**
 * Render JIRA wiki markup as React/MUI components.
 * Supports: h4. headings, * bullets, *bold*, {{code}}, ---- dividers.
 * Unrecognized markup renders as plain text (graceful fallback).
 */
export function renderJiraWiki(text: string): React.ReactNode[] {
  if (!text) return [];
  return text.split('\n').map((line, i) => {
    // h4. headings
    if (line.startsWith('h4. ')) {
      return (
        <Typography
          key={i}
          variant="subtitle2"
          fontWeight="bold"
          sx={{ mt: i > 0 ? 1.5 : 0, mb: 0.5, color: 'primary.main' }}
        >
          {line.slice(4)}
        </Typography>
      );
    }
    // Bullet points
    if (line.startsWith('* ')) {
      return (
        <Box key={i} sx={{ display: 'flex', gap: 0.75, ml: 1, mb: 0.25 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            •
          </Typography>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
            {parseInline(line.slice(2))}
          </Typography>
        </Box>
      );
    }
    // Horizontal rule
    if (line.trim() === '----') {
      return <Divider key={i} sx={{ my: 1 }} />;
    }
    // Empty line
    if (!line.trim()) {
      return <Box key={i} sx={{ height: 8 }} />;
    }
    // Plain text with inline formatting
    return (
      <Typography key={i} variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
        {parseInline(line)}
      </Typography>
    );
  });
}
